// Application-configured RBAC utilities
// This file provides pre-configured RBAC utilities using the app's specific configuration

import { 
  hasPermission as coreHasPermission,
  hasAnyPermission as coreHasAnyPermission,
  hasAllPermissions as coreHasAllPermissions,
  hasRole,
  hasAnyRole
} from '../../core/rbac';
import { PERMISSIONS, ROLES, ROLE_PERMISSIONS } from '../../features/rbac';
import type { Role, User, Permission, RolePermissions } from '../../core/rbac';

/** Target user for hierarchy checks (list/detail payloads may include lineage). */
export type UserManageTarget = User & { CreatedByUserId?: string };

/** Backend may send `CreatedByUserId` or camelCase `createdByUserId`. */
export function getCreatedByUserIdFromPayload(
  target: UserManageTarget & { createdByUserId?: string }
): string | undefined {
  const raw = target.CreatedByUserId ?? target.createdByUserId;
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed.length ? trimmed : undefined;
}

export function samePortalUserId(
  a: string | undefined,
  b: string | undefined | null
): boolean {
  if (a == null || b == null) return false;
  return String(a).trim() === String(b).trim();
}

/** Mutations (edit / suspend / delete / reset password) in user management. */
export function hasUserManagementUpdatePermission(user: User | null): boolean {
  if (!user) return false;
  return (
    hasPermission(user, PERMISSIONS.USERS_UPDATE) ||
    hasPermission(user, PERMISSIONS.USER_UPDATE)
  );
}

// Convert our simplified config to the format expected by core RBAC
const createRolePermissionsConfig = (): Record<Role, RolePermissions> => {
  const config: Record<string, RolePermissions> = {};
  
  Object.entries(ROLE_PERMISSIONS).forEach(([role, permissions]) => {
    config[role] = {
      role: role,
      permissions: permissions as Permission[],
    };
  });
  
  return config;
};

const APP_ROLE_PERMISSIONS_CONFIG = createRolePermissionsConfig();

// Utility to get all permissions for a role using app config
export const getAllPermissionsForRole = (role: Role): Permission[] => {
  const permissions = ROLE_PERMISSIONS[role as keyof typeof ROLE_PERMISSIONS];
  return permissions ? [...permissions] : [];
};

// Utility to get all permissions for a user (role + user-specific)
export const getAllPermissionsForUser = (user: User): Permission[] => {
  const rolePermissions = getAllPermissionsForRole(user.Role);
  const userSpecificPermissions = user.permissions || [];

  // Combine role permissions with user-specific permissions (remove duplicates)
  return [...new Set([...rolePermissions, ...userSpecificPermissions])];
};

// Pre-configured permission checking functions
export const hasPermission = (
  user: User | null,
  permission: Permission
): boolean => {
  if (!user) return false;
  const userPermissions = getAllPermissionsForUser(user);
  return coreHasPermission(user, permission, userPermissions);
};

export const hasAnyPermission = (
  user: User | null,
  permissions: Permission[]
): boolean => {
  if (!user) return false;
  const userPermissions = getAllPermissionsForUser(user);
  return coreHasAnyPermission(user, permissions, userPermissions);
};

export const hasAllPermissions = (
  user: User | null,
  permissions: Permission[]
): boolean => {
  if (!user) return false;
  const userPermissions = getAllPermissionsForUser(user);
  return coreHasAllPermissions(user, permissions, userPermissions);
};

export const getRoleInfo = (role: Role): RolePermissions | undefined => {
  return APP_ROLE_PERMISSIONS_CONFIG[role];
};

// App-specific role hierarchy functions
export const isRoleHigherThan = (role1: Role, role2: Role): boolean => {
  const hierarchy: Record<string, number> = {
    [ROLES.SUPER_ADMIN]: 1,
    [ROLES.TENANT_ADMIN]: 2,
    [ROLES.POWER_ADMIN]: 3,
    [ROLES.NORMAL_USER]: 4,
  };

  return (hierarchy[role1] || 0) > (hierarchy[role2] || 0);
};

/**
 * Open user detail when the row is in scope (uses {@link PERMISSIONS.USER_READ}).
 * Super Admin: all tenant + power admins. Tenant Admin: all power admins in scope (API/tenant).
 * Power Admin: normal users; and power admins they created (same lineage as list scope).
 */
export const canViewUserInManagement = (
  currentUser: (User & { UserId?: string }) | null,
  targetUser: UserManageTarget & { createdByUserId?: string }
): boolean => {
  if (!currentUser) return false;
  if (!hasPermission(currentUser, PERMISSIONS.USER_READ)) return false;

  if (currentUser.Role === ROLES.SUPER_ADMIN) {
    return (
      targetUser.Role === ROLES.TENANT_ADMIN ||
      targetUser.Role === ROLES.POWER_ADMIN
    );
  }

  if (currentUser.Role === ROLES.TENANT_ADMIN) {
    return targetUser.Role === ROLES.POWER_ADMIN;
  }

  if (currentUser.Role === ROLES.POWER_ADMIN) {
    if (targetUser.Role === ROLES.NORMAL_USER) return true;
    if (targetUser.Role === ROLES.POWER_ADMIN) {
      const creatorId = getCreatedByUserIdFromPayload(targetUser);
      if (!currentUser.UserId || creatorId == null) return false;
      return samePortalUserId(currentUser.UserId, creatorId);
    }
    return false;
  }

  return false;
};

/**
 * Whether the current user may edit/delete/suspend another user (scoped by role + creator).
 * Requires `users:update` or `user:update`. Super Admins: all tenant/power admins;
 * Tenant Admins: all power admins in scope (mirrors Super Admin over tenant admins); Power Admins: normal users and power admins they created.
 */
export const canManageUser = (
  currentUser: (User & { UserId?: string }) | null,
  targetUser: UserManageTarget & { createdByUserId?: string }
): boolean => {
  if (!currentUser) return false;

  if (!hasUserManagementUpdatePermission(currentUser)) {
    return false;
  }

  if (currentUser.Role === ROLES.SUPER_ADMIN) {
    return (
      targetUser.Role === ROLES.TENANT_ADMIN ||
      targetUser.Role === ROLES.POWER_ADMIN
    );
  }

  if (currentUser.Role === ROLES.TENANT_ADMIN) {
    return targetUser.Role === ROLES.POWER_ADMIN;
  }

  if (currentUser.Role === ROLES.POWER_ADMIN) {
    if (targetUser.Role === ROLES.NORMAL_USER) return true;
    if (targetUser.Role === ROLES.POWER_ADMIN) {
      const creatorId = getCreatedByUserIdFromPayload(targetUser);
      if (!currentUser.UserId || creatorId == null) return false;
      return samePortalUserId(currentUser.UserId, creatorId);
    }
    return false;
  }

  return false;
};

export const getMissingPermissions = (user: User | null, requiredPermissions: Permission[]): Permission[] => {
  if (!user) return requiredPermissions;
  
  const userPermissions = getAllPermissionsForUser(user);
  return requiredPermissions.filter(permission => !userPermissions.includes(permission));
};

export const canAccessFeature = (user: User | null, featurePermissions: Permission[]): boolean => {
  return hasAnyPermission(user, featurePermissions);
};

// Re-export core role functions that don't need configuration
export { hasRole, hasAnyRole };

// Export app-specific constants for easy access
export { PERMISSIONS, ROLES, ROLE_PERMISSIONS };